import { config } from '@/app/config'
import type { AuthResult } from './types'

type Listener = (result: AuthResult | null) => void

let accessToken: string | null = null
let refreshing: Promise<AuthResult | null> | null = null
const listeners = new Set<Listener>()

export function getAccessToken() {
  return accessToken
}

export function applyAuthResult(result: AuthResult | null) {
  accessToken = result?.accessToken ?? null
  listeners.forEach((listener) => listener(result))
}

export function onSessionChange(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const RETRY_DELAYS = [400, 1200, 2800]

async function requestRefresh(): Promise<AuthResult | null> {
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch(`${config.apiOrigin}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => null)
    if (response?.ok) return (await response.json()) as AuthResult
    if (response && response.status < 500) return null
    const delay = RETRY_DELAYS[attempt]
    if (delay === undefined) throw new Error('Session refresh unavailable')
    await new Promise((resolve) => setTimeout(resolve, delay))
  }
}

export function refreshSession(): Promise<AuthResult | null> {
  refreshing ??= requestRefresh()
    .then((result) => {
      applyAuthResult(result)
      return result
    })
    .catch(() => {
      if (!accessToken) applyAuthResult(null)
      return null
    })
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

export async function logoutSession() {
  await fetch(`${config.apiOrigin}/api/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined)
  applyAuthResult(null)
}
